import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
} from "@mui/material";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useMutation, useQuery } from "react-query";
import useNotification from "../../../hooks/useNotification";
import opname from "../../../services/opname";
import queryDefault from "../../../utils/queryDefault";

interface StockOpnameItem {
  id: number;
  opname_id: number;
  quantity: number;
  category: string;
  status: string;
  createdtime: string;
  createdby: string;
  rejection_reason?: string;
}

interface RiwayatTabProps {
  branchId: number;
}

type StockOpnameFormData = {
  opname_id: number | string;
  quantity: number | string;
  category: string;
  rejection_reason?: string;
};

const getStatusColor = (status: string) => {
  switch (status) {
    case "accepted":
      return "#2e7d32";
    case "rejected":
      return "#d32f2f";
    default:
      return "#1976d2";
  }
};

export default function RiwayatTab({ branchId }: RiwayatTabProps) {
  const notification = useNotification();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const { control, handleSubmit, reset, watch } = useForm<StockOpnameFormData>({
    defaultValues: {
      opname_id: "",
      quantity: "",
      category: "awal",
    },
  });

  const selectedStatus = watch("category");

  const { data: stockData, isLoading, refetch, isRefetching } = useQuery(
    ["opname-riwayat", branchId, page, rowsPerPage],
    () =>
      opname.getStockOpnameList({
        branch_ids: branchId,
        sort_by: "createdtime",
        sort_order: "desc",
        page: page + 1,
        limit: rowsPerPage,
      }),
    {
      ...queryDefault,
      onError: (error: any) => {
        notification.onOpen({
          message: error?.response?.data?.message || "Failed to fetch history",
          type: "error",
          position: "top",
        });
      },
    }
  );

  const { data: opnamesData } = useQuery(
    ["opname-all"],
    () => opname.getOpnameAll(),
    {
      ...queryDefault,
    }
  );

  const createStockMutation = useMutation(
    (data: StockOpnameFormData) => opname.createStockOpname(data),
    {
      onSuccess: () => {
        notification.onOpen({
          message: "Stock opname created successfully",
          type: "success",
          position: "top",
        });
        setOpenDialog(false);
        reset();
        refetch();
      },
      onError: (error: any) => {
        notification.onOpen({
          message: error?.response?.data?.message || "Failed to create stock opname",
          type: "error",
          position: "top",
        });
      },
    }
  );

  const updateStockMutation = useMutation(
    (data: { id: number; payload: StockOpnameFormData }) =>
      opname.updateStockOpname(data.id, data.payload),
    {
      onSuccess: () => {
        notification.onOpen({
          message: "Stock opname updated successfully",
          type: "success",
          position: "top",
        });
        setOpenDialog(false);
        reset();
        setEditingId(null);
        refetch();
      },
      onError: (error: any) => {
        notification.onOpen({
          message: error?.response?.data?.message || "Failed to update stock opname",
          type: "error",
          position: "top",
        });
      },
    }
  );

  const deleteStockMutation = useMutation(
    (id: number) => opname.deleteStockOpname(id),
    {
      onSuccess: () => {
        notification.onOpen({
          message: "Stock opname deleted successfully",
          type: "success",
          position: "top",
        });
        refetch();
      },
      onError: (error: any) => {
        notification.onOpen({
          message: error?.response?.data?.message || "Failed to delete stock opname",
          type: "error",
          position: "top",
        });
      },
    }
  );

  const items: StockOpnameItem[] = stockData?.data || [];
  const total = stockData?.pagination?.total || 0;

  const handleOpenDialog = (item?: StockOpnameItem) => {
    if (item) {
      setEditingId(item.id);
      reset({
        opname_id: item.opname_id,
        quantity: item.quantity,
        category: item.category,
      });
    } else {
      setEditingId(null);
      reset({
        opname_id: "",
        quantity: "",
        category: "awal",
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    reset();
    setEditingId(null);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const onSubmit = (data: StockOpnameFormData) => {
    const payload = {
      opname_id: Number(data.opname_id),
      quantity: Number(data.quantity),
      category: data.category,
    };

    if (editingId) {
      updateStockMutation.mutate({ id: editingId, payload });
    } else {
      createStockMutation.mutate(payload);
    }
  };

  if (isLoading) return <CircularProgress />;

  return (
    <Box>
      <Stack direction="row" justifyContent="flex-end" sx={{ mb: 2 }}>
        <Button variant="contained" onClick={() => handleOpenDialog()}>
          + Create
        </Button>
      </Stack>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
              <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Quantity</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Created</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Created By</TableCell>
              <TableCell sx={{ fontWeight: 600, textAlign: "center" }}>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 3, color: "text.secondary" }}>
                  No history found
                </TableCell>
              </TableRow>
            ) : (
              items.map((item) => (
                <TableRow key={item.id} hover>
                  <TableCell sx={{ textTransform: "capitalize" }}>{item.category}</TableCell>
                  <TableCell>{item.quantity}</TableCell>
                  <TableCell>
                    <Box
                      sx={{
                        display: "inline-block",
                        px: 1.5,
                        py: 0.5,
                        borderRadius: 1,
                        backgroundColor:
                          getStatusColor(item.status) + "20",
                        color: getStatusColor(item.status),
                        fontSize: "0.875rem",
                        fontWeight: 600,
                        textTransform: "capitalize",
                      }}
                    >
                      {item.status}
                    </Box>
                  </TableCell>
                  <TableCell>{new Date(item.createdtime).toLocaleString()}</TableCell>
                  <TableCell>{item.createdby}</TableCell>
                  <TableCell align="center">
                    <Stack direction="row" spacing={1} justifyContent="center">
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => handleOpenDialog(item)}
                        disabled={item.status !== "pending"}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        onClick={() => {
                          if (window.confirm("Are you sure?")) {
                            deleteStockMutation.mutate(item.id);
                          }
                        }}
                      >
                        Delete
                      </Button>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        rowsPerPageOptions={[5, 10, 20, 30, 50, 100]}
        component="div"
        count={total}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingId ? "Edit Stock Opname" : "Create Stock Opname"}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Stack spacing={2}>
            <Controller
              name="opname_id"
              control={control}
              rules={{ required: "Opname is required" }}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  select
                  label="Opname"
                  error={!!error}
                  helperText={error?.message}
                  disabled={!!editingId}
                >
                  {(opnamesData?.data || []).map((item: any) => (
                    <MenuItem key={item.id} value={item.id}>
                      {item.title}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />

            <Controller
              name="quantity"
              control={control}
              rules={{
                required: "Quantity is required",
                min: { value: 1, message: "Must be positive" },
              }}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  label="Quantity"
                  type="number"
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />

            <Controller
              name="category"
              control={control}
              rules={{ required: "Category is required" }}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  select
                  label="Category"
                  error={!!error}
                  helperText={error?.message}
                >
                  <MenuItem value="awal">Awal</MenuItem>
                  <MenuItem value="masuk">Masuk</MenuItem>
                  <MenuItem value="keluar">Keluar</MenuItem>
                  <MenuItem value="minus">Minus</MenuItem>
                </TextField>
              )}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSubmit(onSubmit)}
            variant="contained"
            disabled={createStockMutation.isLoading || updateStockMutation.isLoading}
          >
            {editingId ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
